import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import moment from 'moment';

const safeParse = (value, fallback = []) => {
  if (!value) return fallback;
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }
  return fallback;
};

const PackagesTable = ({ data, loading }) => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const searchInput = useRef(null);
  const EDIT_PATH = '/admin/edit-package';

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex.replace('_', ' ')}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => close()}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : false,
    filterDropdownProps: {
      onOpenChange: (open) => {
        if (open) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      },
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text || 'N/A'
      ),
  });


  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      fixed: 'left',
      render: (id) => id || 'N/A',
    },
    {
      title: 'Package Name',
      dataIndex: 'package_name',
      key: 'package_name',
      width: 150,
      fixed: 'left',
      ...getColumnSearchProps('package_name'),
    },
    {
      title: 'Place Name',
      dataIndex: 'place_name',
      key: 'place_name',
      width: 150,
      ...getColumnSearchProps('place_name'),
    },
    {
      title: 'Days',
      dataIndex: 'days',
      key: 'days',
      width: 80,
    },
    {
      title: 'Nights',
      dataIndex: 'nights',
      key: 'nights',
      width: 80,
    },
    {
      title: 'Highlights',
      dataIndex: 'highlights',
      key: 'highlights',
      width: 300,
      render: (highlights) => {
        const list = safeParse(highlights);
        return list.length ? <ul>{list.map((item, i)=>(<li key={i}>{item}</li>))}</ul> : 'N/A';
      }
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (created_at) =>
        created_at ? moment(created_at).format('YYYY-MM-DD HH:mm:ss') : 'N/A',
    },
     {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 300,
      ellipsis: true,
      render: (description) => description || 'N/A',
    },
    {
      title: 'Overview',
      dataIndex: 'overview',
      key: 'overview',
      width: 300,
      ellipsis: true,
      render: (overview) => overview || 'N/A',
    },
    {
      title: 'Inclusions',
      dataIndex: 'inclusions',
      key: 'inclusions',
      width: 300,
      render: (inclusions) => {
        const list = safeParse(inclusions);
        return list.length ? <ul>{list.map((item,i)=>(<li key={i}>{item}</li>))}</ul> : 'N/A';
      }
    },
    {
      title: 'Exclusions',
      dataIndex: 'exclusions',
      key: 'exclusions',
      width: 300,
      render: (exclusions) => {
      const list = safeParse(exclusions);
      return list.length ? <ul>{list.map((item,i)=>(<li key={i}>{item}</li>))}</ul> : 'N/A';
    }
    },
    {
      title: 'Notes',
      dataIndex: 'note',
      key: 'note',
      width: 300,
      render: (note) => {
      const list = safeParse(note);
      return list.length ? <ul>{list.map((item,i)=>(<li key={i}>{item}</li>))}</ul> : 'N/A';
    }

    },
    {
        title: 'Day Details',
        dataIndex: 'day_details',
        key: 'day_details',
        width: 400,
      render: (days) => {
      const list = safeParse(days);
      return list.length ? (
        <ul>
          {list.map((day,i)=>(
            <li key={i}>{`Day ${day.day_number}: ${day.heading} - ${day.activity}`}</li>
          ))}
        </ul>
      ) : 'N/A';
    }

    },
    {
        title: 'Place Details',
        dataIndex: 'place_details',
        key: 'place_details',
        width: 200,
        render: (places) => {
        const list = safeParse(places);
        return list.length ? (
          <ul>
            {list.map((p,i)=>(<li key={i}>{`${p.place} (${p.nights} nights)`}</li>))}
          </ul>
        ) : 'N/A';
      }
    },
     {
        title: 'Tags',
        dataIndex: 'tags',
        key: 'tags',
        width: 200,
        render: (tags) => {
          const list = safeParse(tags);
          return list.length ? <ul>{list.map((t,i)=>(<li key={i}>{t}</li>))}</ul> : 'N/A';
        }

    },
    {
      title: 'Action',
      key: 'operation',
      fixed: 'right',
      width: 100,
      render: (text, record) => (
        <Button type="link" size="small" 
          onClick={() => {
            navigate(`${EDIT_PATH}/${record.id}`, { state: { packageData: record } });
          }}
          aria-label={`View package ${record.package_name || record.id}`}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={data.map((item, index) => ({
        ...item,
        key: item.id ? `${item.id}` : `fallback-${index}`,
      })).filter(item => item.id != null)}
      loading={loading}
      scroll={{ x: 2000 }}
      summary={() => (
        <Table.Summary fixed='top'/>
      )}
      sticky={{ offsetHeader: 0 }}
      pagination={{ pageSize: 10}}
      className="sticky-table"
    />
  );
};

export default PackagesTable;